/**
 * App.jsx
 *
 * Root component — page layout, providers, and global overlays.
 *
 * Redesigned in the "Mech-HUD" modern-gaming style: dark carbon-fiber
 * backdrop with drifting cyan/violet glows, an animated grid, angular
 * clipped-corner panels, and robotic buttons. The game itself
 * (SpaceGame.jsx) is untouched at the logic level — only its chrome
 * was reskinned to match, see that file for details.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { ToastProvider } from './context/ToastContext';
import { banner, logo }  from './assets/images';
import GlowText           from './components/GlowText';
import Panel              from './components/Panel';
import SpaceGame          from './components/SpaceGame';
import ArtShowcase        from './components/ArtShowcase';
import ShareModal         from './components/ShareModal';
import SiteFooter         from './components/SiteFooter';
import MintPage           from './components/mint/MintPage';
import TaskSection        from './components/tasks/TaskSection';

// Detail rows shown in the PROJECT_DETAILS panel
const PROJECT_STATS = [
  ['MINT DATE',   'TBA'],
  ['MINT PRICE',  'Below $2'],
  ['LAUNCHPAD',   'HEXABOTS OFFICIAL WEBSITE'],
  ['MINT SUPPLY', '2222'],
];

// Full-bleed game section max width — the game is the centerpiece, so it
// gets noticeably more room than the rest of the content column.
const GAME_MAX_WIDTH = 1700;

function RoboticNavTab({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`btn-robotic ${active ? 'btn-robotic-cyan' : 'btn-robotic-ghost'} px-4 py-2 text-xs`}
      style={{ animation: 'none' }}
    >
      {children}
    </button>
  );
}

/**
 * Map a URL path to a page. Kept deliberately small — a full router is
 * more machinery than three tabs need.
 *
 * /mintpage is the shareable link for the mint terminal; /mint and
 * /Mintpage resolve to the same place so a mistyped or differently-cased
 * link still lands correctly.
 */
function pageFromPath(pathname) {
  const p = (pathname || '/').toLowerCase().replace(/\/+$/, '');
  if (p === '/mintpage' || p === '/mint') return 'mint';
  if (p === '/tasks' || p === '/task') return 'tasks';
  return 'game';
}

const PATH_FOR_PAGE = { game: '/', tasks: '/tasks', mint: '/mintpage' };

export default function App() {
  const [page, setPage] = useState(() =>
    pageFromPath(typeof window !== 'undefined' ? window.location.pathname : '/')
  );

  // Keep the URL and the visible tab in step, so the mint page can be
  // linked directly and the back button behaves as people expect.
  const navigate = useCallback((next) => {
    setPage(next);
    const path = PATH_FOR_PAGE[next] || '/';
    if (window.location.pathname !== path) {
      window.history.pushState({ page: next }, '', path);
    }
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const onPop = () => setPage(pageFromPath(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const [videoBlob, setVideoBlob] = useState(null);
  const [finalScore, setFinalScore] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);

  // Called by SpaceGame when the player reaches TARGET_SCORE (100)
  const onWin = useCallback((blob, score) => {
    setVideoBlob(blob);
    setFinalScore(score || 0);
    setModalOpen(true);
  }, []);

  return (
    <ToastProvider>
      <div className="min-h-screen relative hud-backdrop">

        {/* ── Ambient carbon grid (fixed, covers entire viewport) ── */}
        <div className="hud-grid fixed inset-0" style={{ zIndex: 1, pointerEvents: 'none' }} />

        {/* ── Subtle global scanlines for a HUD-screen feel ── */}
        <div className="hud-scanline fixed inset-0" style={{ zIndex: 60, pointerEvents: 'none' }} />

        {/*
          Vignette — kept very light. It used to reach rgba(2,3,7,0.9) at
          the edges, which crushed anything outside the middle of the
          screen and made panels near the sides look muddy. A faint
          falloff still frames the page without dimming content.
        */}
        <div
          className="pointer-events-none fixed inset-0"
          style={{
            zIndex:     55,
            background: 'radial-gradient(ellipse at center, transparent 68%, rgba(3,6,13,0.32) 100%)',
          }}
        />

        {/* ══════════════════════════════════════════════════════════
            TOP STATUS BAR
        ══════════════════════════════════════════════════════════════ */}
        <div
          className="relative w-full flex items-center justify-between px-4 sm:px-6 md:px-10 lg:px-16 py-4"
          style={{ zIndex: 10, borderBottom: '1px solid var(--steel)' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 overflow-hidden"
              style={{
                border: '1px solid var(--cyan)',
                clipPath: 'polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px)',
                boxShadow: '0 0 14px rgba(0,229,255,0.5)',
              }}
            >
              <img src={logo} alt="HEXABOTS" className="w-full h-full object-cover" />
            </div>
            <GlowText text="HEXABOTS" size={16} variant="cyan" />
          </div>

          <div className="flex items-center gap-3">
            <RoboticNavTab active={page === 'game'} onClick={() => navigate('game')}>GAME</RoboticNavTab>
            <RoboticNavTab active={page === 'tasks'} onClick={() => navigate('tasks')}>TASKS</RoboticNavTab>
            <RoboticNavTab active={page === 'mint'} onClick={() => navigate('mint')}>MINT</RoboticNavTab>
          </div>

          <div className="hidden sm:flex items-center gap-2">
            <span className="status-dot" />
            <span className="font-mono-hud text-dim text-xs tracking-widest">SYSTEM ONLINE</span>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════
            PAGE CONTENT
        ══════════════════════════════════════════════════════════════ */}
        <div
          className="relative w-full max-w-[2200px] mx-auto px-4 sm:px-6 md:px-10 lg:px-16 py-10 md:py-16"
          style={{ zIndex: 10 }}
        >
          <div className="space-y-16">

          {page === 'game' ? (
            <>
            {/* ════ HERO ════════════════════════════════════════════ */}
            <section className="text-center space-y-8">
              <div className="flex flex-col items-center gap-6">
                <div
                  className="mech-panel mech-corners w-full max-w-4xl overflow-hidden"
                  style={{ padding: 4 }}
                >
                  <img
                    src={banner}
                    alt="HEXABOTS — Different Minds. One Community."
                    className="w-full block"
                  />
                </div>

                <div className="hud-chip">
                  <span className="status-dot" />
                  <p className="font-mono-hud responsive-lede" style={{ color: 'var(--text)', margin: 0 }}>
                    Secure your WL SPOT. Survive the asteroid field. Reach 100 Score.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => navigate('mint')}
                  className="btn-robotic btn-robotic-violet cta-button px-6 py-3 text-sm"
                >
                  GO TO MINT TERMINAL
                  <span className="btn-sheen" aria-hidden="true" />
                </button>
              </div>

              {/*
                Full-bleed game section — breaks out of the page's own
                max-width using negative margins to reach 100vw.
              */}
              <div
                style={{
                  marginLeft:  'calc(50% - 50vw)',
                  marginRight: 'calc(50% - 50vw)',
                  width:       '100vw',
                }}
              >
                <div className="mx-auto px-4" style={{ maxWidth: GAME_MAX_WIDTH }}>
                  <SpaceGame onWin={onWin} />
                </div>
              </div>
            </section>

            {/* ════ PROJECT DETAILS ═════════════════════════════════ */}
            <section className="space-y-6">
              <Panel title="DATA LOG // HEXABOTS LORE">
                <div className="space-y-6">
                  <p className="font-mono-hud max-w-3xl mx-auto text-fluid-xl text-center" style={{ color: 'var(--text)' }}>
                    HEXABOTS is a TBA-piece hex-headed, robot-styled NFT collective broadcasting
                    from a forgotten signal dimension. Every bot is a screen —
                    every screen carries an expression never seen on any other unit.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {PROJECT_STATS.map(([key, val]) => (
                      <div
                        key={key}
                        className="mech-panel"
                        style={{ padding: '14px 18px' }}
                      >
                        <div className="font-mono-hud text-dim text-xs tracking-widest">{key}</div>
                        <div className="mt-1">
                          <GlowText text={val} size={16} variant="violet" />
                        </div>
                      </div>
                    ))}
                  </div>

                  <p className="font-mono-hud max-w-3xl mx-auto text-fluid-md text-center text-dim">
                    TBA units will be minted by the team for future collabs, events,
                    and campaigns.
                  </p>
                </div>
              </Panel>
            </section>

            {/* ════ ART ══════════════════════════════════════════════ */}
            <section className="space-y-6">
              <ArtShowcase />
            </section>
            </>
          ) : page === 'tasks' ? (
            <section>
              <TaskSection />
            </section>
          ) : (
            <section>
              <MintPage />
            </section>
          )}

            <SiteFooter />

          </div>
        </div>

        {/* ── Win modal (shown when player scores 100) ── */}
        {modalOpen && (
          <ShareModal
            videoBlob={videoBlob}
            score={finalScore}
            onClose={() => setModalOpen(false)}
          />
        )}

      </div>
    </ToastProvider>
  );
}
